import { useDatadogStore } from '@/stores/datadog';
import { getRequestsStarted, whenRequestsIdle } from '@/utils/request-activity';

type BrandSwitchOutcome = 'completed' | 'aborted' | 'failed';

let latestSwitch: symbol | undefined;

/**
 * Times a brand switch for the `brand_switch` RUM vital. Call the returned fn once navigation
 * resolves; a completed switch is reported when requests go quiet, measured to the last response.
 */
export function startBrandSwitchVital(context: { routeName: string; storeOnly: boolean }) {
  const switchId = Symbol('brand-switch');
  latestSwitch = switchId;
  // Resolve the store now, not after requests settle, so the report reaches the app that switched.
  const datadogStore = useDatadogStore();
  const startTime = Date.now();
  const requestsBefore = getRequestsStarted();

  return async (outcome: BrandSwitchOutcome) => {
    const navigatedAt = Date.now();
    const settledAt = outcome === 'completed' ? await whenRequestsIdle() : navigatedAt;
    let reported: BrandSwitchOutcome | 'superseded' | 'timeout' = outcome;
    if (switchId !== latestSwitch) reported = 'superseded';
    else if (settledAt === null) reported = 'timeout';
    const endTime = Math.max(settledAt ?? Date.now(), navigatedAt);

    datadogStore.addDurationVital('brand_switch', {
      startTime,
      duration: endTime - startTime,
      context: {
        route_name: context.routeName,
        store_only: context.storeOnly,
        outcome: reported,
        requests: getRequestsStarted() - requestsBefore,
      },
    });
  };
}
